import { Alert, Collapse, Typography } from 'antd';
import FormLayout from 'components/FormLayout';
import Recipes from 'containers/GlobalMenuManagement/Items/ItemDrawer/Recipes';
import React, { useMemo } from 'react';
import { collectModifierOptionIds, collectModifierOptionRows } from 'utils/menu';

function ItemModifierOptionRecipes({
  modifiers = [],
  allModifiers = [],
  value = [],
  onChange,
  disabled = false,
  itemRecipeLines = [],
  validationErrors = [],
}) {
  const optionRows = useMemo(
    () => collectModifierOptionRows(modifiers, allModifiers),
    [modifiers, allModifiers],
  );
  const allowedOptionIds = useMemo(
    () => new Set(collectModifierOptionIds(modifiers, allModifiers)),
    [modifiers, allModifiers],
  );

  const recipesByOptionId = useMemo(() => {
    const map = {};
    (value || []).forEach((entry) => {
      const optionId = entry.modifier_option ?? entry.modifier_option_id;
      if (optionId != null && allowedOptionIds.has(optionId)) {
        map[optionId] = entry.recipe_lines || [];
      }
    });
    return map;
  }, [value, allowedOptionIds]);

  const updateOptionRecipes = (modifierOptionId, recipeLines) => {
    const next = [...(value || [])].filter((entry) => {
      const optionId = entry.modifier_option ?? entry.modifier_option_id;
      return optionId == null || allowedOptionIds.has(optionId);
    });
    const index = next.findIndex(
      (entry) => (entry.modifier_option ?? entry.modifier_option_id) === modifierOptionId,
    );
    const payload = { modifier_option: modifierOptionId, recipe_lines: recipeLines };
    if (index >= 0) {
      next[index] = payload;
    } else {
      next.push(payload);
    }
    onChange(next.filter((entry) => (entry.recipe_lines || []).length > 0));
  };

  if (!optionRows.length) {
    return (
      <Typography.Text type="secondary">
        Attach modifiers to this item to configure per-option ingredient recipes.
      </Typography.Text>
    );
  }

  const items = optionRows.map((row) => ({
    key: String(row.modifierOptionId),
    label: (
      <Typography.Text>
        <Typography.Text strong>{row.optionTitle}</Typography.Text>
        <Typography.Text type="secondary">{` — ${row.modifierTitle}`}</Typography.Text>
      </Typography.Text>
    ),
    children: (
      <Recipes
        withSection={false}
        disabled={disabled}
        allowNonPositive
        itemRecipeLines={itemRecipeLines}
        value={recipesByOptionId[row.modifierOptionId] || []}
        onChange={(recipeLines) => updateOptionRecipes(row.modifierOptionId, recipeLines)}
      />
    ),
  }));

  return (
    <FormLayout.Section>
      <Typography.Text strong>Modifier option recipes</Typography.Text>
      <Typography.Paragraph type="secondary" className="mb-3">
        Set ingredient quantities per selectable modifier option (e.g. op1, Extra Sauce).
        Options that only open a nested group do not appear here — configure recipes on the nested choices instead.
      </Typography.Paragraph>
      {validationErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message="Fix modifier option recipes before saving"
          description={(
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}
        />
      )}
      <Collapse items={items} />
    </FormLayout.Section>
  );
}

export default ItemModifierOptionRecipes;
